import React from 'react';

import { css } from '@emotion/react';
import { useMediaQuery } from 'react-responsive';
import { black, gray2, gray6, primary } from 'styles/global_defaults/colors';
import { screenSmMin } from 'styles/global_defaults/media-queries';
import {
  doubleSpacing,
  quarterSpacing,
  standardSpacing,
  threeQuartersSpacing,
} from 'styles/global_defaults/scaffolding';

import NvIcon from 'shared/components/nv-icon';

const getIndicatorStyle = (isMobile: boolean) => css`
  margin: -${standardSpacing}px -${standardSpacing}px 0px -${standardSpacing}px;
  padding: ${threeQuartersSpacing}px ${isMobile ? doubleSpacing : 115}px;
  border-bottom: solid 1px ${gray6};
  justify-content: space-between;
  position: sticky;
  top: -${standardSpacing}px;
  z-index: 1;

  .step {
    align-items: center;
  }

  span {
    font-size: 13px;
    color: ${gray2};
  }

  .little-text {
    font-size: 11px;
  }

  .icon {
    padding-right: 13px;
  }

  .active {
    span {
      color: ${black};
    }
    .icon {
      color: ${primary};
    }
  }
`;

const dividerStyle = css`
  height: 1px;
  width: 15%;
  align-self: center;
  margin: 0px ${quarterSpacing}px;
`;

export type CloneStepConfig = {
  icon: string;
  name: string;
  optionalText?: string;
  className?: string;
};

export type NvCloneStepIndicatorProps = {
  currentStep: number;
  steps: CloneStepConfig[];
  type?: 'course' | 'program';
};

const NvCloneStepIndicator = ({
  currentStep,
  steps,
  type = 'course',
}: NvCloneStepIndicatorProps) => {
  const isMobile = useMediaQuery({
    query: `(max-width: ${screenSmMin}px)`,
  });

  return (
    <div className='form-progress text-gray-4 d-flex semi-bold bg-white' css={getIndicatorStyle(isMobile)}>
      {steps.map((step, index) => {
        const stepNumber = index + 1;
        const isActive = currentStep >= stepNumber;
        const isCompleted = currentStep > stepNumber;
        const iconName = type === 'program' && isCompleted ? 'check' : step.icon;

        return (
          <React.Fragment key={step.name}>
            {index > 0 && <Divider active={isActive} />}
            <div className={`step d-flex ${step.className || ''} ${isActive ? 'active' : ''}`}>
              <NvIcon icon={iconName} size='small' />
              <div>
                <span>{step.name}</span>
                {step.optionalText && <div className='little-text text-gray-3'>{step.optionalText}</div>}
              </div>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
};

type DividerProps = {
  active?: boolean;
};

const Divider = ({ active = false }: DividerProps) => (
  <div
    className={`${active ? 'bg-primary' : 'bg-gray-4'}`}
    css={dividerStyle}
  />
);

export default NvCloneStepIndicator;
